import {
  DEFAULT_SCENARIO,
  isChannelsOverride,
  isMembersOverride,
  isPlanOverride,
  isScenarioActive,
  isService,
} from '../scenario'
import type {
  MocktailChannelSpec,
  MocktailScenario,
  PostCountsByTab,
  PostTabKey,
} from '../scenario'

const STORAGE_KEY = 'mocktail.scenario'

const POST_TAB_KEYS: PostTabKey[] = ['queue', 'drafts', 'sent', 'approvals']

function isJsonRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function isValidPostCounts(value: unknown): value is PostCountsByTab {
  return (
    isJsonRecord(value) &&
    POST_TAB_KEYS.every((key) => {
      const count = value[key]
      return typeof count === 'number' && Number.isInteger(count) && count >= 0
    })
  )
}

const OPTIONAL_STRING_FIELDS = ['name', 'handle', 'avatarUrl'] as const

function isValidOptionalString(value: unknown): boolean {
  return value === undefined || typeof value === 'string'
}

function isValidChannelSpec(value: unknown): value is MocktailChannelSpec {
  return (
    isJsonRecord(value) &&
    typeof value.id === 'string' &&
    typeof value.service === 'string' &&
    isService(value.service) &&
    isValidPostCounts(value.postCounts) &&
    OPTIONAL_STRING_FIELDS.every((key) => isValidOptionalString(value[key]))
  )
}

// The scenario shape has already changed once (a bare channel count became channelSpecs) — a
// stale or hand-edited localStorage blob could carry an old or malformed shape that downstream
// code (the walker, the dialog) assumes is well-typed. Rejecting the whole thing on any mismatch
// is safer than a partial merge, which could silently activate a scenario with garbage fields.
// Exported so scenarioPresetsStore.ts can validate a saved preset's scenario the same way.
export function isValidScenario(value: unknown): value is MocktailScenario {
  return (
    isJsonRecord(value) &&
    typeof value.channels === 'string' &&
    isChannelsOverride(value.channels) &&
    Array.isArray(value.channelSpecs) &&
    value.channelSpecs.length > 0 &&
    value.channelSpecs.every(isValidChannelSpec) &&
    typeof value.members === 'string' &&
    isMembersOverride(value.members) &&
    typeof value.membersCount === 'number' &&
    Number.isInteger(value.membersCount) &&
    value.membersCount > 0 &&
    typeof value.plan === 'string' &&
    isPlanOverride(value.plan)
  )
}

export function readScenario(): MocktailScenario {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return DEFAULT_SCENARIO

    const parsed: unknown = JSON.parse(raw)
    return isValidScenario(parsed) ? parsed : DEFAULT_SCENARIO
  } catch {
    return DEFAULT_SCENARIO
  }
}

export function writeScenario(scenario: MocktailScenario): void {
  if (isScenarioActive(scenario)) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(scenario))
  } else {
    localStorage.removeItem(STORAGE_KEY)
  }
}
